import type { ResumeDto } from "@izicvs/dto";
import { useEffect, useRef, useState } from "react";

import { localizeResumeSectionNames } from "@/client/pages/builder/sidebars/left/sections/shared/section-label";

// A4 width in CSS pixels at 96dpi; the artboard renders pages at this width.
const PAGE_WIDTH = 794;

type Props = {
  resume: ResumeDto;
};

// Live, non-interactive thumbnail: renders the resume in the artboard iframe
// and scales it down to fit the card. Nothing is stored server-side.
export const ResumePreviewFrame = ({ resume }: Props) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.25);

  const sendResume = () => {
    frameRef.current?.contentWindow?.postMessage(
      { type: "SET_RESUME", payload: localizeResumeSectionNames(resume.data) },
      window.location.origin,
    );
  };

  // Keep the scale in sync with the container width.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const update = () => {
      setScale(element.clientWidth / PAGE_WIDTH);
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Re-send whenever the resume data changes (live update).
  useEffect(() => {
    sendResume();
  }, [resume.data]);

  const onLoad = () => {
    sendResume();
    // The artboard message listener can mount just after load; retry once.
    setTimeout(sendResume, 300);
  };

  return (
    <div ref={containerRef} className="relative size-full overflow-hidden rounded bg-white">
      <iframe
        ref={frameRef}
        title={resume.title}
        src="/artboard/preview"
        loading="lazy"
        tabIndex={-1}
        style={{
          width: PAGE_WIDTH,
          height: PAGE_WIDTH * 1.4142,
          transformOrigin: "top left",
          transform: `scale(${scale})`,
          pointerEvents: "none",
          border: 0,
        }}
        onLoad={onLoad}
      />
    </div>
  );
};
